import {
    Box,
    Container,
    Flex,
    Heading,
    Spinner,
    VStack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Button,
    Select,
    Input,
    Text,
    useToast,
    Badge,
    HStack,
    Divider,
    Card,
    CardHeader,
    CardBody,
    IconButton,
    Tooltip,
    Alert,
    AlertIcon
} from '@chakra-ui/react';
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import React, { useContext, useState, useEffect } from 'react';
import Navbar from '../compnents/Navbar';
import Footer from '../compnents/Footer';
import AuthContext from '../context/AuthContext';
import ThemeContext from '../context/ThemeContext';
import axios from 'axios';
import UploadYourFile from './UploadYourFile';
import Loading from '../compnents/Loading';
import FeatureUnlockContext from '../context/FeatureUnlockContext';

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction ? process.env.REACT_APP_API_BASE_URL_PROD : process.env.REACT_APP_API_BASE_URL_DEV;

function ManageCustomers() {
    const { token, user, setUser, logout } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const { resetUnlocks } = useContext(FeatureUnlockContext);
    const [loading, setLoading] = useState(true);
    const toast = useToast();

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';
    const backgroundColor = themeData?.colors?.background || '#F7FAFC';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Theme-aware colors
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const strongTextColor = isDarkTheme ? '#F7FAFC' : 'gray.800';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';
    const inputBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'white';
    const tableHeaderBg = isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'gray.50';
    const tableStripedBg = isDarkTheme ? 'rgba(66, 153, 225, 0.05)' : 'gray.50';
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'blue.50';
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;
    const focusBorderColor = isDarkTheme ? accentColor : primaryColor;

    // Customer Management State
    const [customers, setCustomers] = useState([]);
    const [rms, setRms] = useState([]);
    const [selectedRM, setSelectedRM] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [loadingRMs, setLoadingRMs] = useState(false);
    const [assigningCustomer, setAssigningCustomer] = useState(null);

    // RM Book State
    const [selectedRMForBook, setSelectedRMForBook] = useState('');
    const [rmBook, setRmBook] = useState(null);
    const [loadingRMBook, setLoadingRMBook] = useState(false);

    // Who To Call State
    const [selectedRMForCalls, setSelectedRMForCalls] = useState('');
    const [whoToCallList, setWhoToCallList] = useState([]);
    const [loadingWhoToCall, setLoadingWhoToCall] = useState(false);

    useEffect(() => {
        const fetchUser = async () => {
            if (!token || user) {
                setLoading(false);
                return;
            }

            try {
                const res = await axios.get(`${API_BASE_URL}/auth/check-user`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (res.status === 420) {
                    resetUnlocks();
                    logout();
                    return;
                }

                setUser(res.data);
            } catch (error) {
                console.error('Error fetching user:', error);
                if (error.response?.status === 420) {
                    resetUnlocks();
                    logout();
                }
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [token, user, setUser, logout, resetUnlocks]);

    // Fetch customers and RMs on component mount
    useEffect(() => {
        if (user && token) {
            fetchCustomers();
            fetchRMs();
        }
        // eslint-disable-next-line
    }, [user, token, currentPage, searchQuery]);

    const fetchCustomers = async () => {
        setLoadingCustomers(true);
        try {
            const params = {
                page: currentPage,
                page_size: pageSize,
            };
            if (searchQuery) params.search = searchQuery;

            const res = await axios.get(`${API_BASE_URL}/content/list-customers`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            setCustomers(res.data.customers);
            setTotalCount(res.data.pagination.total_count);
        } catch (error) {
            console.error('Error fetching customers:', error);
            toast({
                title: 'Error fetching customers',
                description: error.response?.data?.message || 'Something went wrong',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingCustomers(false);
        }
    };

    const fetchRMs = async () => {
        setLoadingRMs(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/content/list-rms`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setRms(res.data.rms);
        } catch (error) {
            console.error('Error fetching RMs:', error);
            toast({
                title: 'Error fetching RMs',
                description: error.response?.data?.message || 'Something went wrong',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingRMs(false);
        }
    };

    const handleAssignRM = async (customerId, rmId) => {
        setAssigningCustomer(customerId);
        try {
            const endpoint = customers.find(c => c.customer_id === customerId)?.rm_id
                ? '/content/reassign-rm'
                : '/content/assign-rm';

            const payload = customers.find(c => c.customer_id === customerId)?.rm_id
                ? { customer_id: customerId, new_rm_id: rmId }
                : { customer_id: customerId, rm_id: rmId };

            await axios({
                method: customers.find(c => c.customer_id === customerId)?.rm_id ? 'put' : 'post',
                url: `${API_BASE_URL}${endpoint}`,
                headers: { Authorization: `Bearer ${token}` },
                data: payload
            });

            toast({
                title: 'Success',
                description: `RM ${customers.find(c => c.customer_id === customerId)?.rm_id ? 'reassigned' : 'assigned'} successfully`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            fetchCustomers();
        } catch (error) {
            console.error('Error assigning RM:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to assign RM',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setAssigningCustomer(null);
        }
    };

    const fetchRMBook = async (rmId) => {
        if (!rmId) return;

        setLoadingRMBook(true);
        try {
            const res = await axios.post(`${API_BASE_URL}/content/admin-view-rm-book`,
                { rm_id: rmId },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            setRmBook(res.data);
        } catch (error) {
            console.error('Error fetching RM book:', error);
            toast({
                title: 'Error fetching RM book',
                description: error.response?.data?.message || 'Something went wrong',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingRMBook(false);
        }
    };

    const fetchWhoToCall = async (rmId) => {
        if (!rmId) return;

        setLoadingWhoToCall(true);
        try {
            const res = await axios.post(`${API_BASE_URL}/content/admin-who-to-call-for-a-rm`,
                { rm_id: rmId },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            setWhoToCallList(res.data);
        } catch (error) {
            console.error('Error fetching who to call list:', error);
            toast({
                title: 'Error fetching who to call list',
                description: error.response?.data?.message || 'Something went wrong',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingWhoToCall(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    const getUrgencyColor = (urgency) => {
        switch (urgency?.toLowerCase()) {
            case 'high': return 'red';
            case 'medium': return 'orange';
            case 'low': return 'green';
            default: return 'gray';
        }
    };

    if (!token || (!user && loading)) {
        return <Loading />;
    }

    if (!user) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" justifyContent="center" alignItems="center" bg={backgroundColor}>
                <Spinner size="xl" color={isDarkTheme ? accentColor : primaryColor} />
            </Box>
        );
    }

    if (user && user.noOfFileUploads === 0) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg={backgroundColor}>
                <Navbar />
                <UploadYourFile setUser={setUser} token={token} />
            </Box>
        );
    }

    return (
        <Box minH="100vh" display="flex" flexDirection="column" bg={backgroundColor}>
            <Navbar />
            <Box display="flex" flex="1">
                <Container maxW="container.xl" flex="1" py={8}>
                    <VStack spacing={8} align="stretch">
                        {/* Header */}
                        <Heading 
                            size="lg" 
                            color={headingTextColor}
                        >
                            Manage Customers
                        </Heading>

                        {/* Section 1: Customer Assignment */}
                        <Card 
                            bg={cardBg} 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                            borderRadius="xl"
                        >
                            <CardHeader borderBottom="1px" borderColor={borderColor}>
                                <Heading size="md" color={headingTextColor}>Customer Assignment</Heading>
                            </CardHeader>
                            <CardBody>
                                {/* Search Bar */}
                                <HStack mb={4} spacing={3}>
                                    <Input
                                        placeholder="Search customers..."
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        bg={inputBg}
                                        color={strongTextColor}
                                        borderColor={borderColor}
                                        _hover={{ borderColor: focusBorderColor }}
                                        _focus={{ 
                                            borderColor: focusBorderColor, 
                                            boxShadow: `0 0 0 1px ${focusBorderColor}` 
                                        }}
                                        _placeholder={{ color: mutedTextColor }}
                                        size="md"
                                    />
                                    <Button 
                                        onClick={fetchCustomers} 
                                        bg={primaryColor}
                                        color="white"
                                        leftIcon={<SearchIcon />}
                                        _hover={{ bg: buttonHoverBg }}
                                        flexShrink={0}
                                    >
                                        Search
                                    </Button>
                                </HStack>

                                {loadingCustomers ? (
                                    <Flex justify="center" py={8}>
                                        <Spinner size="lg" color={isDarkTheme ? accentColor : primaryColor} />
                                    </Flex>
                                ) : (
                                    <>
                                        <Box 
                                            overflowX="auto"
                                            borderRadius="md"
                                            borderWidth="1px"
                                            borderColor={borderColor}
                                        >
                                            <Table variant="simple" size="sm">
                                                <Thead bg={tableHeaderBg}>
                                                    <Tr>
                                                        <Th color={labelTextColor} borderColor={borderColor}>Customer ID</Th>
                                                        <Th color={labelTextColor} borderColor={borderColor}>Country</Th>
                                                        <Th color={labelTextColor} borderColor={borderColor}>Age</Th>
                                                        <Th color={labelTextColor} borderColor={borderColor}>Risk Profile</Th>
                                                        <Th color={labelTextColor} borderColor={borderColor}>Current RM</Th>
                                                        <Th color={labelTextColor} borderColor={borderColor}>Assign/Reassign RM</Th>
                                                        <Th color={labelTextColor} borderColor={borderColor}>Action</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {customers.map((customer, idx) => (
                                                        <Tr 
                                                            key={customer.customer_id}
                                                            bg={idx % 2 === 1 ? tableStripedBg : 'transparent'}
                                                        >
                                                            <Td fontWeight="semibold" color={strongTextColor} borderColor={borderColor}>
                                                                {customer.customer_id}
                                                            </Td>
                                                            <Td color={bodyTextColor} borderColor={borderColor}>{customer.country_iso}</Td>
                                                            <Td color={bodyTextColor} borderColor={borderColor}>{customer.age}</Td>
                                                            <Td borderColor={borderColor}>
                                                                <Badge colorScheme={customer.risk_profile > 7 ? 'red' : customer.risk_profile > 4 ? 'orange' : 'green'}>
                                                                    {customer.risk_profile}
                                                                </Badge>
                                                            </Td>
                                                            <Td borderColor={borderColor}>
                                                                {customer.rm_id ? (
                                                                    <Badge colorScheme="blue">
                                                                        {rms.find(rm => rm.rm_id === customer.rm_id)?.full_name || customer.rm_id}
                                                                    </Badge>
                                                                ) : (
                                                                    <Text color={mutedTextColor} fontSize="sm">Not assigned</Text>
                                                                )}
                                                            </Td>
                                                            <Td borderColor={borderColor}>
                                                                <Select
                                                                    placeholder="Select RM"
                                                                    size="sm"
                                                                    value={selectedRM[customer.customer_id] || ''}
                                                                    onChange={(e) => setSelectedRM({ ...selectedRM, [customer.customer_id]: e.target.value })}
                                                                    isDisabled={loadingRMs || assigningCustomer === customer.customer_id}
                                                                    bg={inputBg}
                                                                    color={strongTextColor}
                                                                    borderColor={borderColor}
                                                                    _focus={{ borderColor: focusBorderColor }}
                                                                >
                                                                    {rms.map((rm) => (
                                                                        <option 
                                                                            key={rm.rm_id} 
                                                                            value={rm.rm_id}
                                                                            style={{ background: isDarkTheme ? '#2D3748' : 'white' }}
                                                                        >
                                                                            {rm.full_name}
                                                                        </option>
                                                                    ))}
                                                                </Select>
                                                            </Td>
                                                            <Td borderColor={borderColor}>
                                                                <Button
                                                                    size="sm"
                                                                    colorScheme={customer.rm_id ? 'orange' : 'green'}
                                                                    onClick={() => handleAssignRM(customer.customer_id, selectedRM[customer.customer_id])}
                                                                    isDisabled={!selectedRM[customer.customer_id] || assigningCustomer === customer.customer_id}
                                                                    isLoading={assigningCustomer === customer.customer_id}
                                                                >
                                                                    {customer.rm_id ? 'Reassign' : 'Assign'}
                                                                </Button>
                                                            </Td>
                                                        </Tr>
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </Box>

                                        {/* Pagination */}
                                        <Flex justify="space-between" align="center" mt={4} flexWrap="wrap" gap={4}>
                                            <Text color={bodyTextColor} fontSize="sm">
                                                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} customers
                                            </Text>
                                            <HStack>
                                                <IconButton
                                                    icon={<ChevronLeftIcon />}
                                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                    isDisabled={currentPage === 1}
                                                    aria-label="Previous page"
                                                    size="sm"
                                                />
                                                <Text fontWeight="semibold" color={strongTextColor} fontSize="sm">
                                                    Page {currentPage} of {totalPages}
                                                </Text>
                                                <IconButton
                                                    icon={<ChevronRightIcon />}
                                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                    isDisabled={currentPage === totalPages}
                                                    aria-label="Next page"
                                                    size="sm"
                                                />
                                            </HStack>
                                        </Flex>
                                    </>
                                )}
                            </CardBody>
                        </Card>

                        <Divider borderColor={borderColor} />

                        {/* Section 2: RM Book View */}
                        <Card 
                            bg={cardBg} 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                            borderRadius="xl"
                        >
                            <CardHeader borderBottom="1px" borderColor={borderColor}>
                                <Heading size="md" color={headingTextColor}>View RM Customer Book</Heading>
                            </CardHeader>
                            <CardBody>
                                <HStack mb={4} spacing={3}>
                                    <Select
                                        placeholder="Select RM to view book"
                                        value={selectedRMForBook}
                                        onChange={(e) => setSelectedRMForBook(e.target.value)}
                                        bg={inputBg}
                                        color={strongTextColor}
                                        borderColor={borderColor}
                                        _hover={{ borderColor: focusBorderColor }}
                                        _focus={{ borderColor: focusBorderColor }}
                                    >
                                        {rms.map((rm) => (
                                            <option 
                                                key={rm.rm_id} 
                                                value={rm.rm_id}
                                                style={{ background: isDarkTheme ? '#2D3748' : 'white' }}
                                            >
                                                {rm.full_name}
                                            </option>
                                        ))}
                                    </Select>
                                    <Button
                                        bg={primaryColor}
                                        color="white"
                                        onClick={() => fetchRMBook(selectedRMForBook)}
                                        isDisabled={!selectedRMForBook}
                                        isLoading={loadingRMBook}
                                        _hover={{ bg: buttonHoverBg }}
                                        flexShrink={0}
                                    >
                                        View Book
                                    </Button>
                                </HStack>

                                {loadingRMBook ? (
                                    <Flex justify="center" py={8}>
                                        <Spinner size="lg" color={isDarkTheme ? accentColor : primaryColor} />
                                    </Flex>
                                ) : rmBook ? (
                                    <Box>
                                        <Alert 
                                            status="info" 
                                            mb={4} 
                                            borderRadius="md"
                                            bg={infoBg}
                                            borderWidth="1px"
                                            borderColor={borderColor}
                                        >
                                            <AlertIcon />
                                            <Text color={bodyTextColor}>
                                                <Text as="span" fontWeight="bold" color={strongTextColor}>{rmBook.rm_name}</Text> has {rmBook.customer_count} customer(s)
                                            </Text>
                                        </Alert>
                                        <Box 
                                            overflowX="auto"
                                            borderRadius="md"
                                            borderWidth="1px"
                                            borderColor={borderColor}
                                        >
                                            <Table variant="simple" size="sm">
                                                <Thead bg={tableHeaderBg}>
                                                    <Tr>
                                                        <Th color={labelTextColor} borderColor={borderColor}>Customer ID</Th>
                                                        <Th color={labelTextColor} borderColor={borderColor}>Country</Th>
                                                        <Th color={labelTextColor} borderColor={borderColor}>Age</Th>
                                                        <Th color={labelTextColor} borderColor={borderColor}>Risk Profile</Th>
                                                        <Th color={labelTextColor} borderColor={borderColor}>Created At</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {rmBook.customers?.map((customer, idx) => (
                                                        <Tr 
                                                            key={customer.customer_id}
                                                            bg={idx % 2 === 1 ? tableStripedBg : 'transparent'}
                                                        >
                                                            <Td fontWeight="semibold" color={strongTextColor} borderColor={borderColor}>
                                                                {customer.customer_id}
                                                            </Td>
                                                            <Td color={bodyTextColor} borderColor={borderColor}>{customer.country_iso}</Td>
                                                            <Td color={bodyTextColor} borderColor={borderColor}>{customer.age}</Td>
                                                            <Td borderColor={borderColor}>
                                                                <Badge colorScheme={customer.risk_profile > 7 ? 'red' : customer.risk_profile > 4 ? 'orange' : 'green'}>
                                                                    {customer.risk_profile}
                                                                </Badge>
                                                            </Td>
                                                            <Td color={bodyTextColor} borderColor={borderColor}>
                                                                {new Date(customer.created_at).toLocaleDateString()}
                                                            </Td>
                                                        </Tr>
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Text color={mutedTextColor} textAlign="center" py={8}>
                                        Select an RM to view their customer book
                                    </Text>
                                )}
                            </CardBody>
                        </Card>

                        <Divider borderColor={borderColor} />

                        {/* Section 3: Who To Call */}
                        <Card 
                            bg={cardBg} 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                            borderRadius="xl"
                        >
                            <CardHeader borderBottom="1px" borderColor={borderColor}>
                                <Heading size="md" color={headingTextColor}>Who To Call (Priority List)</Heading>
                            </CardHeader>
                            <CardBody>
                                <HStack mb={4} spacing={3}>
                                    <Select
                                        placeholder="Select RM for who-to-call list"
                                        value={selectedRMForCalls}
                                        onChange={(e) => setSelectedRMForCalls(e.target.value)}
                                        bg={inputBg}
                                        color={strongTextColor}
                                        borderColor={borderColor}
                                        _hover={{ borderColor: focusBorderColor }}
                                        _focus={{ borderColor: focusBorderColor }}
                                    >
                                        {rms.map((rm) => (
                                            <option 
                                                key={rm.rm_id} 
                                                value={rm.rm_id}
                                                style={{ background: isDarkTheme ? '#2D3748' : 'white' }}
                                            >
                                                {rm.full_name}
                                            </option>
                                        ))}
                                    </Select>
                                    <Button
                                        colorScheme="teal"
                                        onClick={() => fetchWhoToCall(selectedRMForCalls)}
                                        isDisabled={!selectedRMForCalls}
                                        isLoading={loadingWhoToCall}
                                        flexShrink={0}
                                    >
                                        Get List
                                    </Button>
                                </HStack>

                                {loadingWhoToCall ? (
                                    <Flex justify="center" py={8}>
                                        <Spinner size="lg" color={isDarkTheme ? accentColor : primaryColor} />
                                    </Flex>
                                ) : whoToCallList.length > 0 ? (
                                    <Box 
                                        overflowX="auto"
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                    >
                                        <Table variant="simple" size="sm">
                                            <Thead bg={tableHeaderBg}>
                                                <Tr>
                                                    <Th color={labelTextColor} borderColor={borderColor}>Customer ID</Th>
                                                    <Th color={labelTextColor} borderColor={borderColor}>Priority Score</Th>
                                                    <Th color={labelTextColor} borderColor={borderColor}>Urgency</Th>
                                                    <Th color={labelTextColor} borderColor={borderColor}>Risk Profile</Th>
                                                    <Th color={labelTextColor} borderColor={borderColor}>Age</Th>
                                                    <Th color={labelTextColor} borderColor={borderColor}>Country</Th>
                                                    <Th color={labelTextColor} borderColor={borderColor}>Recommended Action</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {whoToCallList.map((item, idx) => (
                                                    <Tr 
                                                        key={item.customer_id}
                                                        bg={idx % 2 === 1 ? tableStripedBg : 'transparent'}
                                                    >
                                                        <Td fontWeight="semibold" color={strongTextColor} borderColor={borderColor}>
                                                            {item.customer_id}
                                                        </Td>
                                                        <Td borderColor={borderColor}>
                                                            <Tooltip label={`Priority: ${item.priority_score}`}>
                                                                <Badge colorScheme="purple" fontSize="md">
                                                                    {item.priority_score}
                                                                </Badge>
                                                            </Tooltip>
                                                        </Td>
                                                        <Td borderColor={borderColor}>
                                                            <Badge colorScheme={getUrgencyColor(item.urgency_level)}>
                                                                {item.urgency_level}
                                                            </Badge>
                                                        </Td>
                                                        <Td borderColor={borderColor}>
                                                            <Badge colorScheme={item.risk_profile > 7 ? 'red' : item.risk_profile > 4 ? 'orange' : 'green'}>
                                                                {item.risk_profile}
                                                            </Badge>
                                                        </Td>
                                                        <Td color={bodyTextColor} borderColor={borderColor}>{item.age}</Td>
                                                        <Td color={bodyTextColor} borderColor={borderColor}>{item.country_iso}</Td>
                                                        <Td maxW="200px" borderColor={borderColor}>
                                                            <Text fontSize="sm" isTruncated color={bodyTextColor}>
                                                                {item.recommended_action}
                                                            </Text>
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                ) : (
                                    <Text color={mutedTextColor} textAlign="center" py={8}>
                                        Select an RM to view their priority call list
                                    </Text>
                                )}
                            </CardBody>
                        </Card>
                    </VStack>
                </Container>
            </Box>
            <Footer />
        </Box>
    );
}

export default ManageCustomers;